/**
 * TrackerStatsScope — wraps one tab group's stats views. They read that group's
 * tracker (StatsTrackerProvider). A tracker this deployment has not enabled (a
 * Tres Commas link where tres-commas has no keys — its stats would only answer
 * 503) gets a neutral card instead, and no stats request is sent. While GET
 * /app-env is still loading, such a tracker shows a spinner rather than a card
 * that may be wrong a moment later. Voluum and Binom are always enabled, so
 * their views render at once, exactly as before.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';
import { trackerFromEnum, type TrackerEnum } from '@trafficpulse/shared';

import { LoadingSpinner } from '@/components/ui';
import { useAppEnv } from '@/features/app-env';

import { StatsTrackerProvider } from './stats-tracker-context';
import { TrackerPendingCard } from './TrackerPendingCard';
import { useEnabledTrackers } from './useEnabledTrackers';

export const TRACKER_NOT_CONFIGURED = 'not configured on this deployment';

export const TrackerStatsScope: FC<{ provider: TrackerEnum; children: ReactNode }> = ({
  provider,
  children,
}) => {
  const enabled = useEnabledTrackers();
  const { isLoading } = useAppEnv();
  const tracker = trackerFromEnum(provider);
  if (enabled.includes(tracker)) {
    return <StatsTrackerProvider provider={provider}>{children}</StatsTrackerProvider>;
  }
  if (isLoading) {
    return (
      <div className="flex justify-center py-8" data-tracker={tracker}>
        <LoadingSpinner size="md" />
      </div>
    );
  }
  return <TrackerPendingCard tracker={tracker} message={TRACKER_NOT_CONFIGURED} />;
};
